import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { fetchLayoutScreens } from 'src/hooks';
import type { ApiResponse, ILayout } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

export const useGetAllLayouts = (filters: string[] = []) => {
  return useInfiniteQuery({
    queryKey: queryKeys.layouts.pagination(filters),
    queryFn: ({ pageParam = 1 }) => fetchAllLayouts(pageParam, filters),
    staleTime: Infinity,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
  });
};

const fetchAllLayouts = async (page: number, filters: string[] = []) => {
  const query = concat(filters, ['count=true', `page=${page}`]);
  const url = `/layouts${generateQuery(['layoutTypes'], query)}`;

  const response = await requestCCApi({ url });

  const layoutIDs: number[] = response.data.map(
    (layout: ApiResponse) => layout.id,
  );

  const layoutScreens = await queryClient.fetchQuery({
    queryKey: queryKeys.layoutScreens.all(layoutIDs.join('|')),
    queryFn: () => fetchLayoutScreens(layoutIDs),
    staleTime: Infinity,
  });

  const layoutScreensMap = new Map(
    layoutScreens.map((layoutScreen) => [layoutScreen.id, layoutScreen]),
  );

  return {
    layouts: layoutIDs
      .map((id) => layoutScreensMap.get(id))
      .filter(Boolean) as ILayout[], // Reorder layoutScreens to match the order of layoutIDs
    totalPages: response.totalPages,
    totalCount: response.totalCount,
  };
};

export const useGetAllLayoutsWithoutPagination = (filters: string[] = []) => {
  return useQuery({
    queryKey: queryKeys.layouts.all(filters),
    queryFn: () => fetchAllLayoutsWithoutPagination(filters),
    staleTime: Infinity,
  });
};

const fetchAllLayoutsWithoutPagination = async (filters: string[] = []) => {
  const url = `/layouts${generateQuery(['layoutTypes'], filters)}`;

  const response = await requestCCApi({ url });

  const layoutIDs: number[] = response.data.map(
    (layout: ApiResponse) => layout.id,
  );

  const layoutScreens = await queryClient.fetchQuery({
    queryKey: queryKeys.layoutScreens.all(layoutIDs.join('|')),
    queryFn: () => fetchLayoutScreens(layoutIDs),
    staleTime: Infinity,
  });

  const layoutScreensMap = new Map(
    layoutScreens.map((layoutScreen) => [layoutScreen.id, layoutScreen]),
  );

  return {
    layouts: layoutIDs
      .map((id) => layoutScreensMap.get(id))
      .filter(Boolean) as ILayout[],
  };
};
